/**
 * Security utilities for preventing XSS and other vulnerabilities
 */

/**
 * Escape HTML special characters to prevent XSS attacks
 *
 * @param unsafe - Untrusted string that may contain HTML/script tags
 * @returns Sanitized string safe for display in HTML
 *
 * @example
 * escapeHtml('<script>alert("xss")</script>')
 * // Returns: '&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;'
 */
export function escapeHtml(unsafe: string): string {
  if (!unsafe || typeof unsafe !== 'string') {
    return '';
  }

  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Sanitize username for safe display
 * Removes any characters that could be used for XSS
 *
 * @param username - Username to sanitize
 * @returns Safe username for display
 */
export function sanitizeUsername(username: string): string {
  if (!username || typeof username !== 'string') {
    return 'Unknown';
  }

  // Remove any HTML tags
  const withoutTags = username.replace(/<[^>]*>/g, '');

  // Escape remaining HTML entities
  return escapeHtml(withoutTags.trim());
}

/**
 * Validate and sanitize a number input
 * Prevents injection of non-numeric values
 *
 * @param value - Value to validate
 * @param defaultValue - Default value if invalid
 * @returns Safe number
 */
export function sanitizeNumber(value: any, defaultValue: number = 0): number {
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) {
    return defaultValue;
  }
  return num;
}
